import {useMemo, useCallback, useState, useEffect} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

import {getDomains, getIdeaPageDisplay, listMembers, listObjectsFields, listOwners} from "../Api";
import {fetchCategories} from "../services/tagsService";
import {getDeliveryPlatformType} from "../api/integrations";
import {getTags} from "../api/tags";
import {getDevTeams} from "../api/settings";

interface DomainProps {
  accountId: string;
  name: string;
  value: string;
  _id: string;
}

interface DisplayState {
  [value: string]: boolean;
}

interface IdeasTableDisplay {
  pendingSuggestions: any;
  customEvidenceTypes: any;
}

interface UseKnowledgeDataReturn {
  tagCategories: any[];
  deliveryPlatform: string;
  defaultPriority: any;
  defaultStatus: any;
  domains: DomainProps[];
  displayState: DisplayState;
  ideasTableDisplay: IdeasTableDisplay;
  evidenceDomain: boolean;
  tagsList: any[];
  tagsOptions: any[];
  fetchTags: () => void;
  members: any[];
  owners: any[];
  devTeams: any[];
  // Cache management
  clearCache: () => void;
  invalidateFilters: () => void;
  getCacheKey: () => readonly any[];
}

// Query keys factory
const knowledgeDataKeys = {
  all: [] as const,
  config: () => [...knowledgeDataKeys.all, "config"] as const,
  tagCategories: () => [...knowledgeDataKeys.all, "tagCategories"] as const,
  deliveryPlatform: () => [...knowledgeDataKeys.all, "deliveryPlatform"] as const,
  defaultPriority: () => [...knowledgeDataKeys.all, "defaultPriority"] as const,
  defaultStatus: () => [...knowledgeDataKeys.all, "defaultStatus"] as const,
  domains: () => [...knowledgeDataKeys.all, "domains"] as const,
  displayState: () => [...knowledgeDataKeys.all, "displayState"] as const,
  ideasTableDisplay: () => [...knowledgeDataKeys.all, "ideasTableDisplay"] as const,
  evidenceDomain: () => [...knowledgeDataKeys.all, "evidenceDomain"] as const,
  tagsList: () => [...knowledgeDataKeys.all, "tagsList"] as const,
  members: () => [...knowledgeDataKeys.all, "members"] as const,
  owners: () => [...knowledgeDataKeys.all, "owners"] as const,
  devTeams: () => [...knowledgeDataKeys.all, "devTeams"] as const,
  // Helper to invalidate all filter config data
  invalidateAll: () => [...knowledgeDataKeys.all] as const,
};

export const useKnowledgeData = (): UseKnowledgeDataReturn => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const [tagsList, setTagsList] = useState<any[]>([]);
  const [tagsOptions, setTagsOptions] = useState<any[]>([]);

  const filterConfigQueryKey = useMemo(() => {
    return knowledgeDataKeys.config();
  }, []);

  const baseUseQueryConfig = {
    enabled: !!auth0.isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  };

  // Tag categories query
  const {
    data: tagCategories
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.tagCategories(),
    queryFn: async () => {
      const response = await fetchCategories(auth0);
      return response.tagCategories;
    }
  });

  // Delivery platform query
  const {
    data: deliveryPlatform,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.deliveryPlatform(),
    queryFn: async () => getDeliveryPlatformType(auth0)
  });

  // Default priority query
  const {
    data: defaultPriority,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.defaultPriority(),
    queryFn: async () => listObjectsFields("priority", auth0),
    select: (response) => {
      const options =
        (response as any)?.options ||
        (response as any)?.Options ||
        [];
      const def = options.find((opt: any) => opt?.isDefault);
      return def?.label || "Unassigned";
    }
  });

  // Default status query
  const {
    data: defaultStatus,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.defaultStatus(),
    queryFn: async () => listObjectsFields("status", auth0),
    select: (response) => {
      const options =
        (response as any)?.options ||
        (response as any)?.Options ||
        [];
      const def = options.find((opt: any) => opt?.isDefault);
      return def?.label || "Unassigned";
    }
  });

  // Domains query
  const {
    data: domains,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.domains(),
    placeholderData: [],
    queryFn: async () => getDomains(auth0)
  });

  // Display state query
  const {
    data: displayState,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.displayState(),
    queryFn: async () => getIdeaPageDisplay("idea", auth0)
  });

  // Idea table display query
  const {
    data: ideasTableDisplay,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.ideasTableDisplay(),
    queryFn: async () => getIdeaPageDisplay("ideas", auth0)
  });

  // Domain evidence display query
  const {
    data: evidenceDomain,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.evidenceDomain(),
    queryFn: async () => {
      const response = await getIdeaPageDisplay("evidence", auth0);
      return response.domain;
    }
  });

  // Tags query
  const {
    data: tagsListData,
    refetch: fetchTags
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.tagsList(),
    queryFn: async () => getTags(auth0)
  });

  // Members query
  const {
    data: members,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.members(),
    placeholderData: [],
    queryFn: async () => listMembers(auth0),
  });

  // Owners query
  const {
    data: owners,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.owners(),
    placeholderData: [],
    queryFn: async () => listOwners(auth0),
  });

  // Dev teams query
  const {
    data: devTeams,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: knowledgeDataKeys.devTeams(),
    placeholderData: [],
    queryFn: async () => {
      const response = await getDevTeams(auth0);
      return response.teams || [];
    },
  });

  // Cache management callbacks
  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: knowledgeDataKeys.all });
    console.log('🗑️ Knowledge data cache cleared');
  }, [queryClient]);

  const invalidateFilters = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: knowledgeDataKeys.invalidateAll() });
    console.log('🔄 Knowledge data cache invalidated for filter changes');
  }, [queryClient]);

  const getCacheKey = useCallback(() => {
    return filterConfigQueryKey;
  }, [filterConfigQueryKey]);

  useEffect(() => {
    if (tagsListData) {
      setTagsList((tagsListData || []).sort((a: any, b: any) => a.index - b.index));

      // Map tags to options format
      const options = tagsListData
        .sort((a: any, b: any) => a.index - b.index)
        .map((tag: any) => ({
          value: tag._id,
          index: tag.index,
          label: tag.label,
          color: tag.color,
          categoryId: tag.categoryId,
          category: tag.category,
        }))
        .sort((a: any, b: any) => (a?.index || 0) - (b?.index || 0));
      setTagsOptions(options);
    }
  }, [tagsListData]);

  return {
    tagCategories,
    deliveryPlatform,
    defaultPriority,
    defaultStatus,
    domains,
    displayState,
    ideasTableDisplay,
    evidenceDomain,
    tagsList,
    tagsOptions,
    members: members ?? [],
    owners: owners ?? [],
    devTeams: devTeams ?? [],
    fetchTags,
    // Cache management
    clearCache,
    invalidateFilters,
    getCacheKey,
  };
}; 